/**
 * v109.9.122 — Travel & Expense Policy tab (Common Access)
 *
 * Renders the full Travel Policy inside boxed sections with lucide icons in
 * place of bullets/numbers. Every user must click the "I Acknowledge"
 * button once — the parent Travel module gates all other tabs behind this
 * acknowledgment (see `Travel.jsx`).
 */
/* eslint-disable react/jsx-key -- Cells in <IconTable rows={[[..]]}/> are
   spread into keyed <td> elements by the table renderer. */
import { useEffect, useState } from "react";
import {
  BookOpen, Plane, Train, Bus, Car, Hotel, Utensils, Wallet, ShieldCheck,
  Clock, FileCheck2, AlertTriangle, Ban, CheckCircle2, IndianRupee, Bike,
  Building2, Users, Info, Sparkles, Loader2, MapPin, Receipt, Timer,
  Calendar,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import api from "../lib/api";

/* ─────────────────────────── Design primitives ─────────────────────────── */

const SectionCard = ({ index, icon: Icon, title, tint, children }) => {
  const tints = {
    indigo: "from-indigo-50 to-white border-indigo-200 dark:from-indigo-950/30 dark:to-slate-900/80 dark:border-indigo-800/60",
    emerald: "from-emerald-50 to-white border-emerald-200 dark:from-emerald-950/30 dark:to-slate-900/80 dark:border-emerald-800/60",
    amber: "from-amber-50 to-white border-amber-200 dark:from-amber-950/30 dark:to-slate-900/80 dark:border-amber-800/60",
    sky: "from-sky-50 to-white border-sky-200 dark:from-sky-950/30 dark:to-slate-900/80 dark:border-sky-800/60",
    rose: "from-rose-50 to-white border-rose-200 dark:from-rose-950/30 dark:to-slate-900/80 dark:border-rose-800/60",
  };
  const iconTints = {
    indigo: "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300",
    emerald: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
    amber: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
    sky: "bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300",
    rose: "bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300",
  };
  return (
    <section
      className={`rounded-2xl border bg-gradient-to-br ${tints[tint]} p-5 md:p-6 shadow-sm`}
      data-testid={`policy-section-${index}`}
    >
      <header className="flex items-center gap-3 mb-4">
        <span className={`inline-flex h-11 w-11 items-center justify-center rounded-xl ${iconTints[tint]}`}>
          <Icon size={20} strokeWidth={2.2} />
        </span>
        <div>
          <div className="text-[10.5px] font-semibold tracking-widest uppercase text-slate-500 dark:text-slate-400">
            Section {index}
          </div>
          <h2 className="text-lg md:text-xl font-bold text-slate-900 dark:text-slate-100 leading-tight">
            {title}
          </h2>
        </div>
      </header>
      <div className="space-y-4 text-[13.5px] text-slate-700 dark:text-slate-200 leading-relaxed">
        {children}
      </div>
    </section>
  );
};

const SubHeading = ({ icon: Icon, children }) => (
  <h3 className="flex items-center gap-2 text-[13px] font-bold uppercase tracking-wide text-slate-800 dark:text-slate-100 mt-2">
    <Icon size={14} className="text-indigo-600 dark:text-indigo-400" strokeWidth={2.4} />
    {children}
  </h3>
);

/** Item row that replaces a bullet with a lucide icon. */
const Item = ({ icon: Icon = CheckCircle2, iconClass = "text-emerald-600 dark:text-emerald-400", children }) => (
  <div className="flex items-start gap-2.5">
    <Icon size={15} className={`mt-0.5 shrink-0 ${iconClass}`} strokeWidth={2.2} />
    <div className="flex-1">{children}</div>
  </div>
);

const InlineBox = ({ tone = "slate", children }) => {
  const tones = {
    slate: "bg-white/70 dark:bg-slate-900/50 border-slate-200 dark:border-slate-700",
    amber: "bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800/60",
    rose: "bg-rose-50 dark:bg-rose-950/30 border-rose-200 dark:border-rose-800/60",
    emerald: "bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800/60",
  };
  return (
    <div className={`rounded-lg border p-3.5 ${tones[tone]}`}>{children}</div>
  );
};

/** Two-column table with lucide-icon rows. */
const IconTable = ({ headers, rows }) => (
  <div className="overflow-hidden rounded-xl border border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/60">
    <table className="w-full text-[13px]">
      <thead className="bg-slate-100/80 dark:bg-slate-800/70">
        <tr>
          {headers.map((h) => (
            <th key={h} className="text-left px-3.5 py-2 font-semibold text-slate-700 dark:text-slate-200">{h}</th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
        {rows.map((r, i) => (
          <tr key={i} className="hover:bg-slate-50/60 dark:hover:bg-slate-800/40">
            {r.map((cell, j) => (
              <td key={j} className="px-3.5 py-2.5 text-slate-700 dark:text-slate-200 align-top">
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

/* ─────────────────────────────── Main page ─────────────────────────────── */

export default function TravelPolicy({ onAcknowledged }) {
  const [ack, setAck] = useState({ acknowledged: false, acknowledged_at: null, loading: true });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get("/travel-policy/acknowledgment")
      .then(({ data }) => { if (!cancelled) setAck({ ...data, loading: false }); })
      .catch(() => { if (!cancelled) setAck({ acknowledged: false, acknowledged_at: null, loading: false }); });
    return () => { cancelled = true; };
  }, []);

  const acknowledge = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/travel-policy/acknowledgment", {});
      setAck({ ...data, loading: false });
      toast.success("Travel & Expense Policy acknowledged. Thank you.");
      onAcknowledged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save acknowledgment. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6 max-w-5xl mx-auto pb-24" data-testid="travel-policy-page">

      {/* Header banner */}
      <div className="rounded-2xl border border-indigo-200 dark:border-indigo-800/70 bg-gradient-to-br from-indigo-600 to-blue-600 p-6 md:p-7 text-white shadow-lg">
        <div className="flex items-start gap-4">
          <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-white/15 backdrop-blur-sm">
            <BookOpen size={22} strokeWidth={2.2} />
          </span>
          <div className="flex-1">
            <div className="text-[10.5px] uppercase tracking-widest opacity-80 font-semibold">Panchakosh Ventures · HR & Accounts</div>
            <h1 className="text-2xl md:text-3xl font-bold leading-tight">Travel &amp; Expense Policy</h1>
            <p className="mt-2 text-[13.5px] opacity-90 leading-relaxed max-w-2xl">
              Read the full policy below and click <b>I Acknowledge</b> to enable the Travel module. Your acknowledgment is recorded with a timestamp.
            </p>
          </div>
        </div>
      </div>

      {!ack.loading && !ack.acknowledged && (
        <InlineBox tone="amber">
          <div className="flex items-center gap-2 text-amber-800 dark:text-amber-200 font-semibold text-[13px]">
            <AlertTriangle size={16} strokeWidth={2.4} />
            You need to acknowledge this policy before using the Travel module.
          </div>
        </InlineBox>
      )}

      {ack.acknowledged && (
        <InlineBox tone="emerald">
          <div className="flex items-center gap-2 text-emerald-800 dark:text-emerald-200 font-semibold text-[13px]">
            <CheckCircle2 size={16} strokeWidth={2.4} />
            You acknowledged this policy on {new Date(ack.acknowledged_at).toLocaleString()}. Version {ack.version}.
          </div>
        </InlineBox>
      )}

      {/* 1. Travel Advance Policy */}
      <SectionCard index="1" icon={Wallet} title="Travel Advance Policy" tint="indigo">
        <SubHeading icon={Sparkles}>Purpose</SubHeading>
        <Item icon={Info} iconClass="text-indigo-600 dark:text-indigo-400">
          Provides employees financial assistance for approved official travel, while ensuring proper control and accountability of company funds.
        </Item>

        <SubHeading icon={Users}>Eligibility</SubHeading>
        <Item icon={CheckCircle2}>Employees are eligible for a travel advance only for <b>official outstation travel exceeding 1 day</b>.</Item>
        <Item icon={Ban} iconClass="text-rose-500">No travel advance for local travel <b>unless specifically approved by Management</b>.</Item>

        <SubHeading icon={IndianRupee}>Advance Amount</SubHeading>
        <Item icon={CheckCircle2}>Up to <b>80% of the estimated travel expenses</b>, based on the approved travel plan and budget.</Item>
        <InlineBox>
          <div className="text-[12.5px] font-semibold text-slate-600 dark:text-slate-300 mb-2">The advance may include:</div>
          <div className="grid sm:grid-cols-3 gap-2.5">
            <Item icon={Plane}>Travel Cost (Air/Train/Bus)</Item>
            <Item icon={Hotel}>Hotel Accommodation</Item>
            <Item icon={Car}>Local Transportation</Item>
          </div>
        </InlineBox>

        <SubHeading icon={Calendar}>Advance Request Timeline</SubHeading>
        <IconTable
          headers={["Travel Type", "Advance Request Timeline"]}
          rows={[
            [<span className="flex items-center gap-1.5"><Plane size={14} className="text-indigo-500" />Domestic Tour</span>,
             <span className="flex items-center gap-1.5"><Timer size={14} className="text-amber-500" />Minimum 3 Working Days before travel</span>],
            [<span className="flex items-center gap-1.5"><AlertTriangle size={14} className="text-rose-500" />Emergency Travel</span>,
             <span className="flex items-center gap-1.5"><ShieldCheck size={14} className="text-emerald-500" />Subject to Management Approval</span>],
          ]}
        />

        <SubHeading icon={ShieldCheck}>Approval Requirement</SubHeading>
        <Item icon={FileCheck2}>Travel advances are processed <b>only after</b> the official travel request has been approved by the designated Approving Authority. No advance shall be released without the required approvals.</Item>

        <SubHeading icon={AlertTriangle}>Important Guidelines</SubHeading>
        <Item icon={Clock} iconClass="text-amber-500">Requests submitted after the prescribed timeline may not be processed before the travel date.</Item>
        <Item icon={IndianRupee} iconClass="text-slate-500">Employees should request only the amount reasonably required for the official tour.</Item>
        <Item icon={Wallet} iconClass="text-emerald-500">Any unutilized advance must be returned during the Travel Settlement process.</Item>
        <Item icon={Ban} iconClass="text-rose-500">False information or misuse may result in disciplinary action, including recovery of the amount.</Item>
      </SectionCard>

      {/* 2. Travel Expense Settlement Policy */}
      <SectionCard index="2" icon={Receipt} title="Travel Expense Settlement Policy" tint="emerald">
        <SubHeading icon={Sparkles}>Purpose</SubHeading>
        <Item icon={Info} iconClass="text-emerald-600 dark:text-emerald-400">
          Ensures timely reimbursement of official travel expenses and proper settlement of travel advances issued by the Company.
        </Item>

        <SubHeading icon={Users}>Applicability</SubHeading>
        <Item icon={CheckCircle2}>Employees who <b>received a Travel Advance</b> for official travel, or</Item>
        <Item icon={CheckCircle2}>Incurred <b>official business travel expenses</b> eligible for reimbursement.</Item>

        <SubHeading icon={FileCheck2}>How to Submit a Travel Settlement</SubHeading>
        <Item icon={MapPin}>Navigate to <b>Travel Module → Travel Settlement</b> in the Employee Portal.</Item>
        <Item icon={FileCheck2}>Complete the Travel Settlement Form with all required details and upload supporting documents.</Item>

        <SubHeading icon={Timer}>Settlement Timeline</SubHeading>
        <Item icon={Clock} iconClass="text-amber-500">Submit within <b>10 working days</b> from the date of return from the official tour.</Item>
        <Item icon={AlertTriangle} iconClass="text-rose-500">Settlements after 10 working days require <b>prior email approval</b> from the Reporting Manager or the designated Approving Authority.</Item>

        <SubHeading icon={Receipt}>Mandatory Supporting Documents</SubHeading>
        <InlineBox>
          <div className="grid sm:grid-cols-2 gap-2.5">
            <Item icon={FileCheck2}>Completed Travel Expense Claim Form</Item>
            <Item icon={FileCheck2}>Original Bills / Invoices</Item>
            <Item icon={Plane}>Flight Boarding Passes</Item>
            <Item icon={Train}>Train / Bus / Air Tickets</Item>
            <Item icon={Hotel}>Hotel Invoice</Item>
            <Item icon={Car}>Taxi / Cab Bills</Item>
            <Item icon={MapPin}>Toll & Parking Receipts</Item>
            <Item icon={Info}>Any other doc requested by Finance</Item>
          </div>
          <div className="mt-3 text-[12.5px] text-rose-600 dark:text-rose-400 flex items-center gap-1.5">
            <AlertTriangle size={13} strokeWidth={2.4} /> Incomplete submissions may result in delays or rejection.
          </div>
        </InlineBox>

        <SubHeading icon={Wallet}>Unutilized Travel Advance</SubHeading>
        <Item icon={CheckCircle2}>Any unspent/excess advance must be returned <b>at the time</b> of submitting the Travel Settlement.</Item>
        <Item icon={CheckCircle2}>If actuals exceed the advance, the eligible balance will be reimbursed after verification and approval.</Item>

        <SubHeading icon={ShieldCheck}>Verification & Approval</SubHeading>
        <Item icon={FileCheck2}>All claims are verified by the Reporting Manager and Finance/Accounts before reimbursement/adjustment.</Item>
        <Item icon={ShieldCheck}>Company may approve, partially approve, or reject any claim unsupported, over limits, or unrelated to official travel.</Item>

        <SubHeading icon={Ban}>Non-Compliance</SubHeading>
        <Item icon={Ban} iconClass="text-rose-500">False, altered, duplicate, or fabricated bills = misconduct → recovery + disciplinary action.</Item>
        <Item icon={Ban} iconClass="text-rose-500">Failure to settle within timeline = recovery from salary/other dues.</Item>
      </SectionCard>

      {/* 3. Ticket Booking Policy */}
      <SectionCard index="3" icon={Plane} title="Ticket Booking Policy" tint="sky">
        <SubHeading icon={Sparkles}>Purpose</SubHeading>
        <Item icon={Info} iconClass="text-sky-600 dark:text-sky-400">
          Standardized process for booking official travel tickets — cost optimization, timely bookings, and proper approval.
        </Item>

        <SubHeading icon={Building2}>Preferred Booking Method</SubHeading>
        <Item icon={CheckCircle2}>All official travel tickets should be booked via the <b>Company Travel Desk</b>.</Item>
        <Item icon={FileCheck2}>Submit booking requests through <b>Travel Module → Travel Booking</b>.</Item>

        <SubHeading icon={ShieldCheck}>Mandatory Booking Procedure</SubHeading>
        <InlineBox>
          <div className="text-[12.5px] font-semibold text-slate-600 dark:text-slate-300 mb-2">The Travel Booking Module must be used for:</div>
          <div className="grid sm:grid-cols-2 gap-2.5">
            <Item icon={Calendar}>Official travel / training / meetings / installations / school visits / events planned <b>≥ 5 days</b> in advance.</Item>
            <Item icon={Users}>Booking tickets for yourself.</Item>
            <Item icon={Users}>Booking tickets for another employee.</Item>
            <Item icon={Users}>Booking for contractual, temporary, consultant, or internship staff.</Item>
          </div>
        </InlineBox>

        <SubHeading icon={Timer}>Booking Timeline & Required Details</SubHeading>
        <Item icon={CheckCircle2}>Raise the Travel Booking Request <b>immediately</b> after receiving travel approval.</Item>
        <InlineBox>
          <div className="text-[12.5px] font-semibold text-slate-600 dark:text-slate-300 mb-2">Request must include:</div>
          <div className="grid sm:grid-cols-2 gap-2.5">
            <Item icon={Info}>Purpose of Travel</Item>
            <Item icon={MapPin}>Destination</Item>
            <Item icon={Calendar}>Travel Dates</Item>
            <Item icon={Plane}>Preferred Mode of Transport</Item>
            <Item icon={Users}>Employee Name(s)</Item>
            <Item icon={ShieldCheck}>Reporting Manager Approval</Item>
            <Item icon={AlertTriangle}>Any special travel requirements</Item>
          </div>
        </InlineBox>

        <SubHeading icon={Clock}>Travel Desk Response</SubHeading>
        <Item icon={Timer}>Travel Desk will attempt bookings within <b>2 working days</b> of a complete, approved request.</Item>
        <Item icon={AlertTriangle} iconClass="text-amber-500">If no confirmation in 2 working days, coordinate immediately via <b>company chat</b>, <b>WhatsApp group</b>, or the official channel.</Item>

        <SubHeading icon={Info}>Self-Booking of Tickets</SubHeading>
        <InlineBox tone="amber">
          <div className="text-[12.5px] font-semibold text-amber-800 dark:text-amber-200 mb-2">Allowed only in exceptional circumstances:</div>
          <Item icon={AlertTriangle} iconClass="text-amber-600">Emergency travel</Item>
          <Item icon={AlertTriangle} iconClass="text-amber-600">Travel Desk cannot book within required timeline</Item>
          <Item icon={ShieldCheck} iconClass="text-emerald-600">Prior approval from Reporting Manager/Management</Item>
          <div className="mt-2">
            <Item icon={Receipt}>Original tickets and payment proofs must be submitted during Travel Expense Settlement.</Item>
          </div>
        </InlineBox>

        <SubHeading icon={Ban}>Non-Compliance</SubHeading>
        <Item icon={Ban} iconClass="text-rose-500">Self-booked ticket without prior request through the module may be treated as unauthorized.</Item>
        <Item icon={Ban} iconClass="text-rose-500">Company may reject reimbursement / settlement of ticket cost.</Item>
        <Item icon={IndianRupee} iconClass="text-rose-500">Extra cost due to delay may be borne by the employee unless approved by Management.</Item>
      </SectionCard>

      {/* 4. Travel Entitlement & Expense Limits */}
      <SectionCard index="4" icon={ShieldCheck} title="Travel Entitlement & Expense Limits" tint="amber">
        <SubHeading icon={Users}>4.1 Travel Entitlement by Designation</SubHeading>
        <IconTable
          headers={["Designation", "Air Travel", "Train Travel", "Hotel", "Local Travel"]}
          rows={[
            ["Intern / Associate", <span className="text-rose-600 flex items-center gap-1"><Ban size={13}/>Not Permitted</span>, "Sleeper / 3AC", "Budget", "Bus / Metro / Auto"],
            ["Executive", <span className="text-amber-600 flex items-center gap-1"><ShieldCheck size={13}/>Approval Required</span>, "3AC", "Budget", "Auto / Cab"],
            ["Manager", "Economy Class", "2AC / 3AC", "Standard", "Cab"],
            ["Director / COO", "Economy / Flexi", "1AC / 2AC / 3AC", "Premium", "Cab"],
            ["CEO / Founder", <span className="text-emerald-700 flex items-center gap-1"><Sparkles size={13}/>Business Class (Approval)</span>, "1AC", "Premium", "Cab"],
          ]}
        />

        <SubHeading icon={MapPin}>4.2 Local Travel Policy</SubHeading>
        <Item icon={CheckCircle2}>Employees may use public transport, company-approved cabs, or personal vehicles for official travel.</Item>

        <div className="text-[12.5px] font-semibold text-slate-600 dark:text-slate-300 mt-2">Reimbursable Local Travel Expenses</div>
        <div className="grid sm:grid-cols-3 gap-2.5">
          <Item icon={Train}>Metro</Item>
          <Item icon={Bus}>Bus</Item>
          <Item icon={Car}>Auto Rickshaw</Item>
          <Item icon={Car}>Cab / Taxi</Item>
          <Item icon={MapPin}>Parking Charges</Item>
          <Item icon={MapPin}>Toll Charges</Item>
        </div>

        <div className="text-[12.5px] font-semibold text-slate-600 dark:text-slate-300 mt-3">Personal Vehicle Reimbursement (prior manager approval mandatory)</div>
        <IconTable
          headers={["Vehicle Type", "Reimbursement Rate"]}
          rows={[
            [<span className="flex items-center gap-1.5"><Bike size={14} className="text-indigo-500"/>Two Wheeler</span>, <span className="flex items-center gap-1.5"><IndianRupee size={13}/>4 per KM</span>],
            [<span className="flex items-center gap-1.5"><Car size={14} className="text-indigo-500"/>Four Wheeler</span>, <span className="flex items-center gap-1.5"><IndianRupee size={13}/>8 per KM</span>],
          ]}
        />
        <InlineBox>
          <div className="text-[12.5px] font-semibold text-slate-600 dark:text-slate-300 mb-2">Conditions</div>
          <Item icon={ShieldCheck}>Prior Reporting-Manager approval mandatory.</Item>
          <Item icon={MapPin}>Distance calculated via Google Maps/GPS (shortest practical route).</Item>
          <Item icon={CheckCircle2}>Fuel bills not required when claiming per-km reimbursement.</Item>
          <Item icon={Ban} iconClass="text-rose-500">Cannot claim both fuel and per-km reimbursement for the same journey.</Item>
        </InlineBox>

        <SubHeading icon={Plane}>4.3 Travel Mode Priority</SubHeading>
        <Item icon={Info}>Always use the most economical mode based on business requirement. Preferred order:</Item>
        <div className="grid sm:grid-cols-4 gap-2.5">
          <Item icon={Train}>1. Train</Item>
          <Item icon={Plane}>2. Flight (Approval Required)</Item>
          <Item icon={Bus}>3. Bus</Item>
          <Item icon={Car}>4. Cab (only when necessary)</Item>
        </div>
        <Item icon={Clock} iconClass="text-amber-500">Flights should be booked <b>at least 7 days in advance</b> for economical fares.</Item>

        <SubHeading icon={Hotel}>4.4 Hotel Accommodation Limits</SubHeading>
        <IconTable
          headers={["Designation", "Max Hotel Tariff / Night"]}
          rows={[
            ["Intern / Associate", <span className="flex items-center gap-1.5"><IndianRupee size={13}/>1,000</span>],
            ["Executive", <span className="flex items-center gap-1.5"><IndianRupee size={13}/>1,000</span>],
            ["Manager", <span className="flex items-center gap-1.5"><IndianRupee size={13}/>2,000</span>],
            ["Director / COO", <span className="text-amber-600 flex items-center gap-1.5"><ShieldCheck size={13}/>Actual (Approval Required)</span>],
            ["CEO / Founder", <span className="text-amber-600 flex items-center gap-1.5"><ShieldCheck size={13}/>Actual (Approval Required)</span>],
          ]}
        />
        <InlineBox>
          <Item icon={CheckCircle2}>Select economical and business-appropriate accommodation.</Item>
          <Item icon={Utensils}>Complimentary breakfast allowed if included in room tariff.</Item>
          <Item icon={Ban} iconClass="text-rose-500">Mini-bar, alcohol, spa, gym, upgrades, laundry (unless approved), personal expenses are <b>not reimbursable</b>.</Item>
        </InlineBox>

        <SubHeading icon={Utensils}>4.5 Daily Food Allowance</SubHeading>
        <IconTable
          headers={["Designation", "Daily Allowance"]}
          rows={[
            ["Intern / Associate", <span className="flex items-center gap-1.5"><IndianRupee size={13}/>300</span>],
            ["Executive", <span className="flex items-center gap-1.5"><IndianRupee size={13}/>500</span>],
            ["Manager", <span className="flex items-center gap-1.5"><IndianRupee size={13}/>700</span>],
            ["Director / COO", <span className="text-amber-600 flex items-center gap-1.5"><ShieldCheck size={13}/>Actual (Approval Required)</span>],
            ["CEO / Founder", <span className="text-amber-600 flex items-center gap-1.5"><ShieldCheck size={13}/>Actual (Approval Required)</span>],
          ]}
        />
        <Item icon={Info}>If meals are provided by Company/Client/Hotel/Event, no additional allowance is payable for those meals.</Item>

        <SubHeading icon={Ban}>4.6 Non-Reimbursable Expenses</SubHeading>
        <InlineBox tone="rose">
          <div className="grid sm:grid-cols-2 gap-2.5">
            <Item icon={Ban} iconClass="text-rose-500">Alcoholic beverages</Item>
            <Item icon={Ban} iconClass="text-rose-500">Cigarettes / tobacco / related</Item>
            <Item icon={Ban} iconClass="text-rose-500">Personal shopping</Item>
            <Item icon={Ban} iconClass="text-rose-500">Entertainment expenses</Item>
            <Item icon={Ban} iconClass="text-rose-500">Family / accompanying-person expenses</Item>
            <Item icon={Ban} iconClass="text-rose-500">Traffic fines / challans</Item>
            <Item icon={Ban} iconClass="text-rose-500">Personal mobile recharge / SIM</Item>
            <Item icon={Ban} iconClass="text-rose-500">Laundry (except tours &gt; 5 consecutive days, with approval)</Item>
            <Item icon={Ban} iconClass="text-rose-500">Tips / gratuities (unless approved)</Item>
          </div>
        </InlineBox>

        <SubHeading icon={ShieldCheck}>4.7 General Travel Guidelines</SubHeading>
        <Item icon={CheckCircle2}>Always choose the most economical and reasonable mode of travel.</Item>
        <Item icon={CheckCircle2}>Economy Class preferred for air travel unless approved otherwise.</Item>
        <Item icon={CheckCircle2}>Use company-approved vendors and negotiated rates where available.</Item>
        <Item icon={Users}>Employees attending the same meeting/training/event may need to share accommodation where practical.</Item>
        <Item icon={FileCheck2}>All claims are subject to verification and approval by the Company.</Item>
        <Item icon={Ban} iconClass="text-rose-500">False/duplicate/altered/inflated claims → recovery, disciplinary action, and any other action the Company deems appropriate.</Item>
      </SectionCard>

      {/* Acknowledge sticky footer */}
      <div className="sticky bottom-0 z-10 pt-4 pb-3 -mx-4 px-4 md:mx-0 md:px-0 md:pt-6">
        <div className="rounded-2xl border border-indigo-200 dark:border-indigo-800/70 bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm p-4 md:p-5 shadow-lg">
          <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-5">
            <div className="flex-1">
              <div className="text-[13px] font-semibold text-slate-800 dark:text-slate-100 flex items-center gap-1.5">
                <ShieldCheck size={15} className="text-indigo-600" />
                {ack.acknowledged ? "Policy already acknowledged" : "By clicking Acknowledge, you confirm that you have read and understood the Travel & Expense Policy."}
              </div>
              {!ack.acknowledged && (
                <div className="text-[12px] text-slate-500 dark:text-slate-400 mt-0.5">This will be timestamped and recorded against your employee ID.</div>
              )}
            </div>
            <Button
              disabled={submitting || ack.loading || ack.acknowledged}
              onClick={acknowledge}
              className={`min-w-[180px] h-11 text-[13.5px] font-semibold ${ack.acknowledged ? "bg-emerald-600 hover:bg-emerald-600" : "bg-indigo-600 hover:bg-indigo-700"} text-white`}
              data-testid="policy-acknowledge-btn"
            >
              {submitting ? (<><Loader2 size={15} className="mr-2 animate-spin" /> Saving…</>)
                : ack.acknowledged ? (<><CheckCircle2 size={15} className="mr-2" /> Acknowledged</>)
                : (<><CheckCircle2 size={15} className="mr-2" /> I Acknowledge</>)}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
